import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Paper,
    Select,
    Skeleton,
    Snackbar,
    Switch,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Tabs,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LockResetIcon from '@mui/icons-material/LockReset';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../layout/AppLayout';
import AuthContext from '../../context/AuthContext';
import { twoFactorService, userService } from '../../services/api';
import { INK, MUTED, RULE, formatCount, formatINR } from '../analytics/analyticsUi';

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------
const GREEN = '#1BAA52';
const AMBER = '#C77700';
const RED = '#C2410C';
const TEAL = '#0E7C86';

const ROLE_LABELS = { admin: 'Admin', user: 'User', reseller: 'Reseller' };
const roleLabel = (role) => ROLE_LABELS[role] || role || 'Unknown';

const STATUS_FILTERS = [
    { value: '', label: 'All' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' }
];

const ACCOUNT_STATUS = {
    active: { label: 'Active', color: GREEN },
    pending: { label: 'Pending', color: AMBER },
    suspended: { label: 'Suspended', color: RED }
};
const VERIFICATION_STATUS = {
    verified: { label: 'Verified', color: GREEN },
    in_progress: { label: 'In progress', color: TEAL },
    unverified: { label: 'Not verified', color: AMBER },
    rejected: { label: 'Rejected', color: RED }
};
const BILLING_STATUS = {
    active: { label: 'Active', color: GREEN },
    pending: { label: 'Awaiting payment', color: AMBER },
    cancelled: { label: 'Cancelled', color: RED },
    expired: { label: 'Expired', color: MUTED }
};
const MESSAGE_STATUS = {
    queued: { label: 'Queued', color: MUTED },
    sent: { label: 'Sent', color: TEAL },
    delivered: { label: 'Delivered', color: GREEN },
    read: { label: 'Read', color: GREEN },
    failed: { label: 'Failed', color: RED }
};

const metaFor = (table, key) => table[key] || { label: key ? capitalize(key) : 'Unknown', color: MUTED };
const capitalize = (text) => (text ? `${text.charAt(0).toUpperCase()}${text.slice(1).replace(/_/g, ' ')}` : '');

const formatDate = (value) =>
    value ? new Date(value).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '–';
const formatDateTime = (value) =>
    value
        ? new Date(value).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' })
        : '–';
const formatMoney = (amount, currency = 'INR') => {
    if (currency === 'INR') return formatINR(amount);
    try {
        return new Intl.NumberFormat('en-IN', { style: 'currency', currency, maximumFractionDigits: 2 }).format(amount || 0);
    } catch (e) {
        return `${currency} ${Number(amount || 0).toFixed(2)}`;
    }
};

const EMPTY_FORM = { name: '', email: '', password: '', company: '', phone: '', gstin: '', tallyLedgerName: '', role: 'user', isActive: true };
const buttonSx = { textTransform: 'none' };
const headRowSx = { '& th': { color: MUTED, fontWeight: 500, borderColor: RULE, py: 1.25, whiteSpace: 'nowrap' } };
const bodyRowSx = { '& td': { borderColor: RULE, py: 1.25 } };

// ---------------------------------------------------------------------------
// Small pieces
// ---------------------------------------------------------------------------
const StatusDot = ({ color, label, caption }) => (
    <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
            <Typography variant="body2" sx={{ color: INK, fontWeight: 500, whiteSpace: 'nowrap' }}>{label}</Typography>
        </Box>
        {caption && (
            <Typography variant="caption" sx={{ color: MUTED, display: 'block', pl: 2 }}>{caption}</Typography>
        )}
    </Box>
);

const Muted = ({ children, ...rest }) => (
    <Typography variant="body2" sx={{ color: MUTED }} {...rest}>{children}</Typography>
);

const RowActions = ({ user, isSelf, onView, onEdit, onDelete, onResetTwoFactor }) => {
    const [anchor, setAnchor] = useState(null);
    const close = () => setAnchor(null);

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
            <Button size="small" onClick={() => onView(user)} sx={{ ...buttonSx, fontWeight: 600, whiteSpace: 'nowrap' }}>
                View details
            </Button>
            <IconButton
                size="small"
                onClick={(e) => setAnchor(e.currentTarget)}
                aria-label={`More actions for ${user.name}`}
                aria-haspopup="menu"
            >
                <MoreVertIcon fontSize="small" />
            </IconButton>
            <Menu
                anchorEl={anchor}
                open={!!anchor}
                onClose={close}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <MenuItem onClick={() => { close(); onEdit(user); }}>
                    <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Edit user" />
                </MenuItem>
                {user.twoFactorEnabled && (
                    <MenuItem onClick={() => { close(); onResetTwoFactor(user); }}>
                        <ListItemIcon><LockResetIcon fontSize="small" /></ListItemIcon>
                        <ListItemText primary="Reset two-step verification" secondary="For a lost phone" />
                    </MenuItem>
                )}
                <MenuItem onClick={() => { close(); onDelete(user); }} disabled={isSelf} sx={{ color: RED }}>
                    <ListItemIcon><DeleteOutlineIcon fontSize="small" sx={{ color: RED }} /></ListItemIcon>
                    <ListItemText primary="Delete user" secondary={isSelf ? "You can't delete yourself" : null} />
                </MenuItem>
            </Menu>
        </Box>
    );
};

// A framed block used for loading, error and empty states
const StateFrame = ({ children }) => (
    <Paper variant="outlined" sx={{ borderColor: RULE, borderRadius: 2, overflow: 'hidden' }}>{children}</Paper>
);

// ---------------------------------------------------------------------------
// Add / edit dialog
// ---------------------------------------------------------------------------
const UserFormDialog = ({ open, mode, initial, onClose, onSubmit }) => {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState(null);
    const isAdd = mode === 'add';

    useEffect(() => {
        if (open) {
            setForm(initial || EMPTY_FORM);
            setFormError(null);
            setSaving(false);
        }
    }, [open, initial]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const passwordTooShort = isAdd && form.password.length > 0 && form.password.length < 6;
    const canSubmit =
        form.name.trim() &&
        form.email.trim() &&
        form.company.trim() &&
        form.phone.trim() &&
        (!isAdd || form.password.length >= 6);

    const submit = async (e) => {
        e.preventDefault();
        if (!canSubmit || saving) return;
        setSaving(true);
        setFormError(null);
        const result = await onSubmit(form);
        if (result?.error) {
            setFormError(result.error);
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="sm" fullWidth fullScreen={fullScreen}>
            <Box component="form" onSubmit={submit} noValidate>
                <DialogTitle sx={{ color: INK, fontWeight: 600 }}>
                    {isAdd ? 'Add user' : `Edit ${initial?.name || 'user'}`}
                </DialogTitle>
                <DialogContent>
                    {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
                    <Grid container spacing={2} sx={{ pt: 0.5 }}>
                        <Grid item xs={12} sm={6}>
                            <TextField name="name" label="Full name" fullWidth size="small" required autoFocus
                                value={form.name} onChange={handleChange} inputProps={{ maxLength: 50 }} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField name="email" label="Email" type="email" fullWidth size="small" required
                                value={form.email} onChange={handleChange} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField name="company" label="Company" fullWidth size="small" required
                                value={form.company} onChange={handleChange} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField name="phone" label="Phone number" fullWidth size="small" required
                                value={form.phone} onChange={handleChange} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField name="gstin" label="GSTIN (optional)" fullWidth size="small"
                                value={form.gstin || ''} onChange={handleChange}
                                inputProps={{ maxLength: 15, style: { textTransform: 'uppercase' } }}
                                helperText="Printed on the customer's invoices" />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField name="tallyLedgerName" label="Tally ledger name (optional)" fullWidth size="small"
                                value={form.tallyLedgerName || ''} onChange={handleChange}
                                helperText="Exact ledger name used in the Tally export; company name if empty" />
                        </Grid>
                        {isAdd && (
                            <Grid item xs={12} sm={6}>
                                <TextField name="password" label="Password" type="password" fullWidth size="small" required
                                    autoComplete="new-password"
                                    value={form.password} onChange={handleChange}
                                    error={passwordTooShort}
                                    helperText={passwordTooShort ? 'Use at least 6 characters' : 'At least 6 characters'} />
                            </Grid>
                        )}
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel id="user-form-role">Role</InputLabel>
                                <Select labelId="user-form-role" name="role" label="Role" value={form.role} onChange={handleChange}>
                                    <MenuItem value="user">User</MenuItem>
                                    <MenuItem value="admin">Admin</MenuItem>
                                    <MenuItem value="reseller">Reseller</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={<Switch checked={form.isActive} onChange={handleChange} name="isActive" />}
                                label={
                                    <Box>
                                        <Typography variant="body2" sx={{ color: INK }}>Active</Typography>
                                        <Typography variant="caption" sx={{ color: MUTED }}>
                                            Inactive users can't sign in to the portal.
                                        </Typography>
                                    </Box>
                                }
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={onClose} disabled={saving} sx={buttonSx}>Cancel</Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={!canSubmit || saving}
                        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                        sx={{ ...buttonSx, fontWeight: 600 }}
                    >
                        {saving ? 'Saving…' : isAdd ? 'Add user' : 'Save changes'}
                    </Button>
                </DialogActions>
            </Box>
        </Dialog>
    );
};

// ---------------------------------------------------------------------------
// Details dialog
// ---------------------------------------------------------------------------
const DetailTable = ({ minWidth, head, children }) => (
    <TableContainer sx={{ border: `1px solid ${RULE}`, borderRadius: 2 }}>
        <Table size="small" sx={{ minWidth }}>
            <TableHead>
                <TableRow sx={headRowSx}>{head}</TableRow>
            </TableHead>
            <TableBody>{children}</TableBody>
        </Table>
    </TableContainer>
);

const DetailEmpty = ({ children }) => (
    <Box sx={{ py: 4, px: 1 }}>
        <Muted>{children}</Muted>
    </Box>
);

const UserDetailsDialog = ({ open, user, details, loading, error, onRetry, onClose, onEdit, onAssignSubscription }) => {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const [tab, setTab] = useState(0);

    useEffect(() => {
        if (open) setTab(0);
    }, [open]);

    const accounts = details?.accounts || [];
    const billings = details?.billings || [];
    const messages = details?.messages || [];
    const profile = details?.user || user;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={fullScreen}>
            <DialogTitle sx={{ pb: 1 }}>
                <Typography component="span" sx={{ display: 'block', color: INK, fontWeight: 600, fontSize: '1.15rem' }}>
                    {profile?.name || 'User details'}
                </Typography>
                {profile && (
                    <Typography component="span" variant="body2" sx={{ display: 'block', color: MUTED, mt: 0.25, wordBreak: 'break-word' }}>
                        {[profile.email, profile.company, profile.phone].filter(Boolean).join(' · ')}
                    </Typography>
                )}
            </DialogTitle>
            <DialogContent>
                {profile && (
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' },
                            gap: 2,
                            py: 1.5,
                            mb: 1,
                            borderTop: `1px solid ${RULE}`,
                            borderBottom: `1px solid ${RULE}`
                        }}
                    >
                        <Box>
                            <Typography variant="caption" sx={{ color: MUTED }}>Role</Typography>
                            <Typography variant="body2" sx={{ color: INK, fontWeight: 500 }}>{roleLabel(profile.role)}</Typography>
                        </Box>
                        <Box>
                            <Typography variant="caption" sx={{ color: MUTED }}>Status</Typography>
                            <StatusDot color={profile.isActive ? GREEN : RED} label={profile.isActive ? 'Active' : 'Inactive'} />
                            <Typography variant="caption" sx={{ color: MUTED, display: 'block' }}>
                                Two-step verification {profile.twoFactorEnabled ? 'on' : 'off'}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="caption" sx={{ color: MUTED }}>Last login</Typography>
                            <Typography variant="body2" sx={{ color: INK, fontVariantNumeric: 'tabular-nums' }}>
                                {profile.lastLogin ? formatDateTime(profile.lastLogin) : 'Never'}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="caption" sx={{ color: MUTED }}>Created</Typography>
                            <Typography variant="body2" sx={{ color: INK, fontVariantNumeric: 'tabular-nums' }}>
                                {formatDate(profile.createdAt)}
                            </Typography>
                        </Box>
                    </Box>
                )}

                {loading ? (
                    <Box aria-busy="true" sx={{ pt: 2 }}>
                        <Skeleton width="50%" height={36} />
                        {[0, 1, 2].map((i) => <Skeleton key={i} height={32} />)}
                    </Box>
                ) : error ? (
                    <Box sx={{ py: 3 }}>
                        <Typography sx={{ fontWeight: 600, color: INK, mb: 1 }}>Details could not be loaded</Typography>
                        <Typography variant="body2" sx={{ color: MUTED, mb: 2 }}>{error}</Typography>
                        <Button variant="outlined" onClick={onRetry} sx={buttonSx}>Try again</Button>
                    </Box>
                ) : (
                    <>
                        <Box sx={{ borderBottom: `1px solid ${RULE}`, mb: 2 }}>
                            <Tabs
                                value={tab}
                                onChange={(e, value) => setTab(value)}
                                variant="scrollable"
                                allowScrollButtonsMobile
                                aria-label="User details"
                                sx={{ '& .MuiTab-root': { textTransform: 'none', minHeight: 44 } }}
                            >
                                <Tab label={`WhatsApp accounts (${formatCount(accounts.length)})`} id="user-tab-0" aria-controls="user-tabpanel-0" />
                                <Tab label={`Subscriptions (${formatCount(billings.length)})`} id="user-tab-1" aria-controls="user-tabpanel-1" />
                                <Tab label={`Messages (${formatCount(messages.length)})`} id="user-tab-2" aria-controls="user-tabpanel-2" />
                            </Tabs>
                        </Box>

                        <TabPanel value={tab} index={0}>
                            {accounts.length ? (
                                <DetailTable
                                    minWidth={520}
                                    head={<><TableCell>Account</TableCell><TableCell>Status</TableCell><TableCell>Verification</TableCell></>}
                                >
                                    {accounts.map((account) => {
                                        const status = metaFor(ACCOUNT_STATUS, account.status);
                                        const verification = metaFor(VERIFICATION_STATUS, account.verificationStatus);
                                        return (
                                            <TableRow key={account._id} sx={bodyRowSx}>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ color: INK, fontWeight: 500 }}>{account.businessName || '–'}</Typography>
                                                    <Typography variant="caption" sx={{ color: MUTED, fontVariantNumeric: 'tabular-nums' }}>{account.phoneNumber}</Typography>
                                                </TableCell>
                                                <TableCell><StatusDot color={status.color} label={status.label} /></TableCell>
                                                <TableCell><StatusDot color={verification.color} label={verification.label} /></TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </DetailTable>
                            ) : (
                                <DetailEmpty>This user hasn't connected a WhatsApp Business account yet.</DetailEmpty>
                            )}
                        </TabPanel>

                        <TabPanel value={tab} index={1}>
                            {billings.length ? (
                                <DetailTable
                                    minWidth={560}
                                    head={
                                        <>
                                            <TableCell>Plan</TableCell>
                                            <TableCell align="right">Amount</TableCell>
                                            <TableCell>Period</TableCell>
                                            <TableCell>Status</TableCell>
                                        </>
                                    }
                                >
                                    {billings.map((billing) => {
                                        const status = metaFor(BILLING_STATUS, billing.status);
                                        return (
                                            <TableRow key={billing._id} sx={bodyRowSx}>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ color: INK, fontWeight: 500 }}>{capitalize(billing.plan)}</Typography>
                                                    {billing.billingCycle && (
                                                        <Typography variant="caption" sx={{ color: MUTED }}>{capitalize(billing.billingCycle)}</Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: INK, whiteSpace: 'nowrap' }}>
                                                    {formatMoney(billing.amount, billing.currency)}
                                                </TableCell>
                                                <TableCell sx={{ fontVariantNumeric: 'tabular-nums', color: INK, whiteSpace: 'nowrap' }}>
                                                    {formatDate(billing.startDate)} – {formatDate(billing.endDate)}
                                                </TableCell>
                                                <TableCell><StatusDot color={status.color} label={status.label} /></TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </DetailTable>
                            ) : (
                                <DetailEmpty>No subscriptions yet. They appear here once this user picks a plan.</DetailEmpty>
                            )}
                        </TabPanel>

                        <TabPanel value={tab} index={2}>
                            {messages.length ? (
                                <DetailTable
                                    minWidth={520}
                                    head={
                                        <>
                                            <TableCell>Recipient</TableCell>
                                            <TableCell>Type</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell>Sent</TableCell>
                                        </>
                                    }
                                >
                                    {messages.map((message) => {
                                        const status = metaFor(MESSAGE_STATUS, message.status);
                                        return (
                                            <TableRow key={message._id} sx={bodyRowSx}>
                                                <TableCell sx={{ fontVariantNumeric: 'tabular-nums', color: INK }}>{message.recipient}</TableCell>
                                                <TableCell sx={{ color: INK }}>{capitalize(message.messageType)}</TableCell>
                                                <TableCell><StatusDot color={status.color} label={status.label} /></TableCell>
                                                <TableCell sx={{ fontVariantNumeric: 'tabular-nums', color: message.sentAt ? INK : MUTED, whiteSpace: 'nowrap' }}>
                                                    {message.sentAt ? formatDateTime(message.sentAt) : 'Not sent'}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </DetailTable>
                            ) : (
                                <DetailEmpty>No messages recorded for this user. The 50 most recent appear here.</DetailEmpty>
                            )}
                        </TabPanel>
                    </>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                {profile && (
                    <Button onClick={() => onAssignSubscription(profile)} sx={buttonSx}>Assign subscription</Button>
                )}
                {profile && (
                    <Button onClick={() => onEdit(profile)} sx={buttonSx}>Edit user</Button>
                )}
                <Button onClick={onClose} variant="outlined" sx={buttonSx}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

function TabPanel({ children, value, index }) {
    return (
        <div role="tabpanel" hidden={value !== index} id={`user-tabpanel-${index}`} aria-labelledby={`user-tab-${index}`}>
            {value === index && children}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
const UserManagement = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { user: currentUser } = useContext(AuthContext);
    const currentUserId = currentUser?._id || currentUser?.id;

    const [users, setUsers] = useState([]);
    const [totalUsers, setTotalUsers] = useState(0);
    const [adminCount, setAdminCount] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);

    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [roleFilter, setRoleFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [formDialog, setFormDialog] = useState({ open: false, mode: 'add', user: null });
    const [pendingDelete, setPendingDelete] = useState(null);
    const [pendingReset, setPendingReset] = useState(null);
    const [resetting, setResetting] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [detailsUser, setDetailsUser] = useState(null);
    const [details, setDetails] = useState(null);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [detailsError, setDetailsError] = useState(null);

    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    const hasFilters = !!(search || searchInput || roleFilter || statusFilter);

    useEffect(() => {
        if (currentUser && currentUser.role !== 'admin') navigate('/dashboard');
    }, [currentUser, navigate]);

    // Debounce the search box so every keystroke doesn't hit the API
    useEffect(() => {
        const timer = setTimeout(() => {
            setSearch(searchInput.trim());
            setPage(0);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchInput]);

    const fetchUsers = useCallback(async () => {
        try {
            setLoading(true);
            setLoadError(null);
            const params = { page: page + 1, limit: rowsPerPage };
            if (search) params.search = search;
            if (roleFilter) params.role = roleFilter;
            if (statusFilter) params.status = statusFilter;
            const response = await userService.getUsers(params);
            setUsers(response.data || []);
            setTotalUsers(response.total ?? (response.data || []).length);
        } catch (err) {
            setLoadError(err.response?.data?.error || 'Check your connection and try again.');
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, search, roleFilter, statusFilter]);

    const fetchAdminCount = useCallback(async () => {
        try {
            const response = await userService.getUsers({ page: 1, limit: 1, role: 'admin' });
            setAdminCount(response.total ?? null);
        } catch (err) {
            setAdminCount(null);
        }
    }, []);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    useEffect(() => {
        fetchAdminCount();
    }, [fetchAdminCount]);

    const refreshAll = () => {
        fetchUsers();
        fetchAdminCount();
    };

    const clearFilters = () => {
        setSearchInput('');
        setSearch('');
        setRoleFilter('');
        setStatusFilter('');
        setPage(0);
    };

    // Details
    const loadDetails = useCallback(async (target) => {
        try {
            setDetailsLoading(true);
            setDetailsError(null);
            const response = await userService.getUserDetails(target._id);
            setDetails(response.data);
        } catch (err) {
            setDetailsError(err.response?.data?.error || 'Check your connection and try again.');
        } finally {
            setDetailsLoading(false);
        }
    }, []);

    const openDetails = (target) => {
        setDetailsUser(target);
        setDetails(null);
        loadDetails(target);
    };

    const closeDetails = () => {
        setDetailsUser(null);
        setDetails(null);
        setDetailsError(null);
    };

    // Add / edit
    const openAdd = () => setFormDialog({ open: true, mode: 'add', user: null });
    const openEdit = (target) => {
        closeDetails();
        setFormDialog({ open: true, mode: 'edit', user: target });
    };
    const closeForm = () => setFormDialog((prev) => ({ ...prev, open: false }));

    const formInitial = useMemo(() => {
        const target = formDialog.user;
        if (formDialog.mode !== 'edit' || !target) return EMPTY_FORM;
        return {
            name: target.name || '',
            email: target.email || '',
            password: '',
            company: target.company || '',
            phone: target.phone || '',
            gstin: target.gstin || '',
            tallyLedgerName: target.tallyLedgerName || '',
            role: target.role || 'user',
            isActive: target.isActive !== false
        };
    }, [formDialog.mode, formDialog.user]);

    const submitForm = async (form) => {
        try {
            if (formDialog.mode === 'add') {
                await userService.createUser(form);
                setSuccess(`${form.name} added`);
            } else {
                const { password, ...rest } = form;
                await userService.updateUser(formDialog.user._id, rest);
                setSuccess(`${form.name} updated`);
            }
            closeForm();
            refreshAll();
            return null;
        } catch (err) {
            const fallback = formDialog.mode === 'add'
                ? 'The user could not be added. Check the details and try again.'
                : 'Your changes could not be saved. Try again.';
            return { error: err.response?.data?.error || fallback };
        }
    };

    // Delete
    const confirmDelete = async () => {
        if (!pendingDelete) return;
        try {
            setDeleting(true);
            await userService.deleteUser(pendingDelete._id);
            setSuccess(`${pendingDelete.name} deleted`);
            setPendingDelete(null);
            if (users.length === 1 && page > 0) setPage(page - 1);
            else fetchUsers();
            fetchAdminCount();
        } catch (err) {
            setError(err.response?.data?.error || 'The user could not be deleted. Try again.');
        } finally {
            setDeleting(false);
        }
    };

    // Two-step verification reset, for a user who lost their phone and recovery codes
    const confirmReset = async () => {
        if (!pendingReset) return;
        try {
            setResetting(true);
            await twoFactorService.reset(pendingReset._id);
            setUsers((prev) => prev.map((u) => (u._id === pendingReset._id ? { ...u, twoFactorEnabled: false } : u)));
            setSuccess(`Two-step verification turned off for ${pendingReset.name}`);
            setPendingReset(null);
        } catch (err) {
            setError(err.response?.data?.error || 'Two-step verification could not be reset. Try again.');
        } finally {
            setResetting(false);
        }
    };

    const actionProps = { onView: openDetails, onEdit: openEdit, onDelete: setPendingDelete, onResetTwoFactor: setPendingReset };
    const isSelf = (target) => !!currentUserId && String(target._id) === String(currentUserId);

    // -----------------------------------------------------------------------
    // List body
    // -----------------------------------------------------------------------
    const renderList = () => {
        if (loading && !users.length) {
            return (
                <StateFrame>
                    <Box sx={{ p: { xs: 2, sm: 3 } }} aria-busy="true">
                        {[0, 1, 2, 3, 4].map((i) => (
                            <Box key={i} sx={{ display: 'flex', gap: 3, py: 1.5, borderTop: i ? `1px solid ${RULE}` : 'none' }}>
                                <Box sx={{ flex: 2 }}><Skeleton width="55%" /><Skeleton width="40%" /></Box>
                                <Box sx={{ flex: 2, display: { xs: 'none', md: 'block' } }}><Skeleton width="60%" /><Skeleton width="45%" /></Box>
                                <Box sx={{ flex: 1 }}><Skeleton width="50%" /></Box>
                                <Box sx={{ flex: 1, display: { xs: 'none', md: 'block' } }}><Skeleton width="70%" /></Box>
                            </Box>
                        ))}
                    </Box>
                </StateFrame>
            );
        }

        if (loadError && !users.length) {
            return (
                <StateFrame>
                    <Box sx={{ p: { xs: 3, sm: 5 } }}>
                        <Typography sx={{ fontWeight: 600, color: INK, mb: 1 }}>Users could not be loaded</Typography>
                        <Typography variant="body2" sx={{ color: MUTED, mb: 2.5, maxWidth: '60ch' }}>{loadError}</Typography>
                        <Button variant="outlined" onClick={fetchUsers} sx={buttonSx}>Try again</Button>
                    </Box>
                </StateFrame>
            );
        }

        if (!users.length) {
            return (
                <StateFrame>
                    <Box sx={{ p: { xs: 3, sm: 5 } }}>
                        {hasFilters ? (
                            <>
                                <Typography sx={{ fontWeight: 600, color: INK, mb: 1 }}>No users match these filters</Typography>
                                <Typography variant="body2" sx={{ color: MUTED, mb: 2.5 }}>
                                    Try a different name, email or company, or clear the filters to see everyone.
                                </Typography>
                                <Button variant="outlined" onClick={clearFilters} sx={buttonSx}>Clear filters</Button>
                            </>
                        ) : (
                            <>
                                <Typography sx={{ fontWeight: 600, color: INK, mb: 1 }}>No users yet</Typography>
                                <Typography variant="body2" sx={{ color: MUTED, mb: 2.5, maxWidth: '60ch' }}>
                                    Add a user to give a customer access to the portal. They can then connect WhatsApp Business accounts and pick a plan.
                                </Typography>
                                <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd} sx={{ ...buttonSx, fontWeight: 600 }}>
                                    Add user
                                </Button>
                            </>
                        )}
                    </Box>
                </StateFrame>
            );
        }

        const pagination = (
            <TablePagination
                component="div"
                count={totalUsers}
                page={page}
                onPageChange={(e, next) => setPage(next)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[5, 10, 25, 50]}
                labelRowsPerPage="Per page"
                sx={{ borderTop: `1px solid ${RULE}`, color: MUTED }}
            />
        );

        const statusOf = (target) => (target.isActive
            ? { color: GREEN, label: 'Active' }
            : { color: RED, label: 'Inactive' });

        if (isMobile) {
            return (
                <StateFrame>
                    <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, opacity: loading ? 0.6 : 1 }} aria-busy={loading}>
                        {users.map((target, index) => {
                            const status = statusOf(target);
                            return (
                                <Box component="li" key={target._id} sx={{ px: 2, py: 1.75, borderTop: index ? `1px solid ${RULE}` : 'none' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5 }}>
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography sx={{ color: INK, fontWeight: 600 }} noWrap>
                                                {target.name}{isSelf(target) ? ' (you)' : ''}
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: MUTED, wordBreak: 'break-all' }}>{target.email}</Typography>
                                        </Box>
                                        <StatusDot color={status.color} label={status.label} caption={roleLabel(target.role)} />
                                    </Box>
                                    <Typography variant="body2" sx={{ color: INK, mt: 0.75 }}>
                                        {target.company || '–'}
                                        {target.phone && (
                                            <Box component="span" sx={{ color: MUTED, fontVariantNumeric: 'tabular-nums' }}> · {target.phone}</Box>
                                        )}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: MUTED, display: 'block', mt: 0.5 }}>
                                        Last login {target.lastLogin ? formatDate(target.lastLogin) : 'never'} · Created {formatDate(target.createdAt)}
                                    </Typography>
                                    <Box sx={{ mt: 1, ml: -1 }}>
                                        <RowActions user={target} isSelf={isSelf(target)} {...actionProps} />
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                    {pagination}
                </StateFrame>
            );
        }

        return (
            <StateFrame>
                <TableContainer sx={{ opacity: loading ? 0.6 : 1, transition: 'opacity 120ms' }} aria-busy={loading}>
                    <Table size="small" sx={{ minWidth: 900 }}>
                        <TableHead>
                            <TableRow sx={headRowSx}>
                                <TableCell sx={{ pl: 2 }}>User</TableCell>
                                <TableCell>Company</TableCell>
                                <TableCell>Role</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Last login</TableCell>
                                <TableCell>Created</TableCell>
                                <TableCell align="right" sx={{ pr: 2 }}>
                                    <Box component="span" sx={visuallyHidden}>Actions</Box>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {users.map((target) => {
                                const status = statusOf(target);
                                return (
                                    <TableRow key={target._id} hover sx={{ '& td': { borderColor: RULE, py: 1.25 } }}>
                                        <TableCell sx={{ pl: 2, maxWidth: 260 }}>
                                            <Typography variant="body2" sx={{ color: INK, fontWeight: 600 }} noWrap title={target.name}>
                                                {target.name}
                                                {isSelf(target) && <Box component="span" sx={{ color: MUTED, fontWeight: 400 }}> (you)</Box>}
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: MUTED }} noWrap title={target.email}>{target.email}</Typography>
                                        </TableCell>
                                        <TableCell sx={{ maxWidth: 220 }}>
                                            <Typography variant="body2" sx={{ color: INK }} noWrap title={target.company}>{target.company || '–'}</Typography>
                                            <Typography variant="body2" sx={{ color: MUTED, fontVariantNumeric: 'tabular-nums' }} noWrap>
                                                {target.phone || 'No phone'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ color: INK, fontWeight: target.role === 'admin' ? 600 : 400 }}>
                                                {roleLabel(target.role)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell><StatusDot color={status.color} label={status.label} /></TableCell>
                                        <TableCell sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', color: target.lastLogin ? INK : MUTED }}>
                                            {target.lastLogin ? formatDate(target.lastLogin) : 'Never'}
                                        </TableCell>
                                        <TableCell sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', color: INK }}>
                                            {formatDate(target.createdAt)}
                                        </TableCell>
                                        <TableCell align="right" sx={{ pr: 1 }}>
                                            <RowActions user={target} isSelf={isSelf(target)} {...actionProps} />
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
                {pagination}
            </StateFrame>
        );
    };

    const subline = () => {
        if (loading && !users.length) return 'Loading users…';
        if (loadError && !users.length) return 'Users unavailable';
        const count = `${formatCount(totalUsers)} ${totalUsers === 1 ? 'user' : 'users'}`;
        if (hasFilters) return `${count} match your filters`;
        return adminCount !== null ? `${count} · ${formatCount(adminCount)} ${adminCount === 1 ? 'admin' : 'admins'}` : count;
    };

    return (
        <AppLayout>
            <Container maxWidth="lg" sx={{ pb: 6 }}>
                {/* Header */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-end', gap: 2, mb: 3 }}>
                    <Box>
                        <Typography
                            component="h1"
                            sx={{ fontSize: { xs: '1.75rem', md: '2.25rem' }, fontWeight: 700, letterSpacing: '-0.025em', color: INK, lineHeight: 1.15 }}
                        >
                            Users
                        </Typography>
                        <Typography variant="body2" sx={{ color: MUTED, mt: 0.75 }}>{subline()}</Typography>
                    </Box>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd} sx={{ ...buttonSx, fontWeight: 600 }}>
                        Add user
                    </Button>
                </Box>

                {/* Toolbar */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: { xs: 1.5, md: 2 }, mb: 2 }}>
                    <TextField
                        size="small"
                        placeholder="Search name, email or company"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        inputProps={{ 'aria-label': 'Search users' }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" sx={{ color: MUTED }} />
                                </InputAdornment>
                            ),
                            endAdornment: searchInput ? (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => setSearchInput('')} aria-label="Clear search">
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ) : null
                        }}
                        sx={{ flex: { xs: '1 1 100%', md: '1 1 320px' }, maxWidth: { md: 420 }, bgcolor: 'background.paper' }}
                    />

                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={statusFilter}
                        onChange={(e, value) => { if (value !== null) { setStatusFilter(value); setPage(0); } }}
                        aria-label="Filter by status"
                    >
                        {STATUS_FILTERS.map((option) => (
                            <ToggleButton key={option.label} value={option.value} sx={{ textTransform: 'none', px: 1.5 }}>
                                {option.label}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>

                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel id="role-filter-label">Role</InputLabel>
                        <Select
                            labelId="role-filter-label"
                            label="Role"
                            value={roleFilter}
                            onChange={(e) => { setRoleFilter(e.target.value); setPage(0); }}
                        >
                            <MenuItem value="">Any role</MenuItem>
                            <MenuItem value="admin">Admin</MenuItem>
                            <MenuItem value="user">User</MenuItem>
                            <MenuItem value="reseller">Reseller</MenuItem>
                        </Select>
                    </FormControl>

                    {hasFilters && (
                        <Button size="small" onClick={clearFilters} sx={{ ...buttonSx, color: MUTED }}>
                            Clear filters
                        </Button>
                    )}
                </Box>

                {renderList()}

                <UserFormDialog
                    open={formDialog.open}
                    mode={formDialog.mode}
                    initial={formInitial}
                    onClose={closeForm}
                    onSubmit={submitForm}
                />

                <UserDetailsDialog
                    open={!!detailsUser}
                    user={detailsUser}
                    details={details}
                    loading={detailsLoading}
                    error={detailsError}
                    onRetry={() => detailsUser && loadDetails(detailsUser)}
                    onAssignSubscription={(profile) => navigate('/admin/subscriptions', { state: { assignUserId: profile._id } })}
                    onClose={closeDetails}
                    onEdit={openEdit}
                />

                {/* Two-step verification reset */}
                <Dialog open={!!pendingReset} onClose={() => !resetting && setPendingReset(null)} maxWidth="xs" fullWidth>
                    <DialogTitle sx={{ color: INK, fontWeight: 600 }}>Reset two-step verification for {pendingReset?.name}?</DialogTitle>
                    <DialogContent>
                        <Typography variant="body2" sx={{ color: MUTED }}>
                            {pendingReset?.email} can then sign in with their password only, and set up two-step verification again
                            from their profile. Only do this after confirming who is asking, for example by calling them.
                        </Typography>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                        <Button onClick={() => setPendingReset(null)} disabled={resetting} sx={buttonSx}>Cancel</Button>
                        <Button
                            onClick={confirmReset}
                            variant="contained"
                            disabled={resetting}
                            startIcon={resetting ? <CircularProgress size={16} color="inherit" /> : null}
                            sx={buttonSx}
                        >
                            {resetting ? 'Resetting…' : 'Reset'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Delete confirmation */}
                <Dialog open={!!pendingDelete} onClose={() => !deleting && setPendingDelete(null)} maxWidth="xs" fullWidth>
                    <DialogTitle sx={{ color: INK, fontWeight: 600 }}>Delete {pendingDelete?.name}?</DialogTitle>
                    <DialogContent>
                        <Typography variant="body2" sx={{ color: MUTED }}>
                            {pendingDelete?.email} loses access to the portal straight away. Their WhatsApp accounts, subscriptions and
                            messages stay in the database. This can't be undone.
                        </Typography>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                        <Button onClick={() => setPendingDelete(null)} disabled={deleting} sx={buttonSx}>Cancel</Button>
                        <Button
                            onClick={confirmDelete}
                            variant="contained"
                            color="error"
                            disabled={deleting}
                            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : null}
                            sx={buttonSx}
                        >
                            {deleting ? 'Deleting…' : 'Delete user'}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                    <Alert onClose={() => setError(null)} severity="error" variant="filled" sx={{ width: '100%' }}>{error}</Alert>
                </Snackbar>
                <Snackbar open={!!success} autoHideDuration={4000} onClose={() => setSuccess(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                    <Alert onClose={() => setSuccess(null)} severity="success" variant="filled" sx={{ width: '100%' }}>{success}</Alert>
                </Snackbar>
            </Container>
        </AppLayout>
    );
};

const visuallyHidden = {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    whiteSpace: 'nowrap',
    width: 1
};

export default UserManagement;
